/* ==========================================================================
   Catalog Assignment Modal Component
   Allows pharmacy admins to assign specific catalog products to a provider
   ========================================================================== */

import { useState, useEffect, useMemo } from 'react'
import { Search } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import type { CatalogProductResponse, ProviderResponse } from '@/api/types'
import { compareCategories } from '@/utils/sort'

/* --------------------------------------------------------------------------
   Props
   -------------------------------------------------------------------------- */

export interface CatalogAssignmentModalProps {
  open: boolean
  onClose: () => void
  onSave: (productIds: string[]) => Promise<void>
  provider: ProviderResponse | null
  products: CatalogProductResponse[]
  assignedProductIds: string[]
  loading?: boolean
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function CatalogAssignmentModal({
  open,
  onClose,
  onSave,
  provider,
  products,
  assignedProductIds,
  loading = false,
}: CatalogAssignmentModalProps) {
  const [isCustom, setIsCustom] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      const hasAssignments = assignedProductIds.length > 0
      setIsCustom(hasAssignments)
      setSelectedIds(new Set(assignedProductIds))
      setSearchQuery('')
    }
  }, [open, assignedProductIds])

  // Filter only active products and group by category
  const activeProducts = useMemo(() => {
    return products.filter((p) => p.is_active)
  }, [products])

  // Filter by search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return activeProducts
    const query = searchQuery.toLowerCase()
    return activeProducts.filter(
      (p) =>
        p.display_name.toLowerCase().includes(query) ||
        p.product_code.toLowerCase().includes(query) ||
        p.categories.some((c) => c.name.toLowerCase().includes(query))
    )
  }, [activeProducts, searchQuery])

  // Group by the product's full category list (joined), matching how the
  // legacy single-string categories displayed here before PORTAL-77 —
  // multi-category products show one combined header, not just the first.
  const groupedProducts = useMemo(() => {
    const groups: Record<string, CatalogProductResponse[]> = {}
    for (const product of filteredProducts) {
      const category =
        product.categories.map((c) => c.name).join(' / ') || 'Uncategorized'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(product)
    }
    // Sort categories alphabetically, but keep Uncategorized last
    return Object.entries(groups).sort(([a], [b]) => compareCategories(a, b))
  }, [filteredProducts])

  function handleToggleProduct(productId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  // Selection state for a category, based only on its currently-visible products
  function getCategoryState(
    categoryProducts: CatalogProductResponse[]
  ): 'all' | 'some' | 'none' {
    const selectedCount = categoryProducts.filter((p) =>
      selectedIds.has(p.id)
    ).length
    if (selectedCount === 0) return 'none'
    if (selectedCount === categoryProducts.length) return 'all'
    return 'some'
  }

  function handleToggleCategory(categoryProducts: CatalogProductResponse[]) {
    const isAllSelected = getCategoryState(categoryProducts) === 'all'
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const product of categoryProducts) {
        if (isAllSelected) {
          next.delete(product.id)
        } else {
          next.add(product.id)
        }
      }
      return next
    })
  }

  function handleSelectAll() {
    setSelectedIds(new Set(activeProducts.map((p) => p.id)))
  }

  function handleDeselectAll() {
    setSelectedIds(new Set())
  }

  async function handleSave() {
    if (isCustom) {
      await onSave(Array.from(selectedIds))
    } else {
      // Default mode: remove all assignments
      await onSave([])
    }
  }

  const providerName = provider?.name || provider?.email || 'Provider'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage Catalog"
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Configure which catalog products{' '}
          <strong className="text-charcoal">{providerName}</strong> can see
          when submitting prescriptions.
        </p>

        {/* Toggle: Default vs Custom */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setIsCustom(false)}
            className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
              !isCustom
                ? 'border-deep-purple bg-soft-lavender text-deep-purple'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            All Products
            <span className="block text-xs font-normal mt-0.5 opacity-70">
              Provider sees entire active catalog
            </span>
          </button>
          <button
            type="button"
            onClick={() => setIsCustom(true)}
            className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
              isCustom
                ? 'border-deep-purple bg-soft-lavender text-deep-purple'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            Custom Catalog
            <span className="block text-xs font-normal mt-0.5 opacity-70">
              Provider sees only selected products
            </span>
          </button>
        </div>

        {/* Product selection (only shown in custom mode) */}
        {isCustom && (
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Select all / Deselect all */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {selectedIds.size} of {activeProducts.length} products selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-deep-purple hover:underline text-xs"
                >
                  Select all
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="text-deep-purple hover:underline text-xs"
                >
                  Deselect all
                </button>
              </div>
            </div>

            {/* Product list */}
            <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No products found matching your search.
                </div>
              ) : (
                groupedProducts.map(([category, categoryProducts]) => {
                  const categoryState = getCategoryState(categoryProducts)
                  return (
                  <div key={category}>
                    <label className="sticky top-0 z-10 flex items-center gap-3 bg-gray-50 px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={categoryState === 'all'}
                        ref={(el) => {
                          if (el) el.indeterminate = categoryState === 'some'
                        }}
                        onChange={() => handleToggleCategory(categoryProducts)}
                        className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-deep-purple"
                      />
                      {category}
                    </label>
                    {categoryProducts.map((product) => (
                      <label
                        key={product.id}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(product.id)}
                          onChange={() => handleToggleProduct(product.id)}
                          className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-deep-purple"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-charcoal font-medium truncate">
                            {product.display_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {product.product_code}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  )
                })
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            loading={loading}
            className="sm:flex-1"
          >
            Save Assignments
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default CatalogAssignmentModal
